import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

/* ============== Shared types ============== */
type SortOrder = "asc" | "desc";
type DateRange = { from: Date | null; to: Date | null };

type LoadStatus = "idle" | "loading" | "success" | "error";
type AsyncData<T> = {
  status: LoadStatus;
  data?: T;
  error?: unknown;
  updatedAt?: number;
};

/* ============== Dropdowns (filters) ============== */
type Option<V extends string | number> = {
  value: V;
  label: string;
};

type OptionsValues<
  TModes extends Record<string, SelectMode>,
  TSelects extends Record<string, string | number>,
> = {
  [K in keyof TModes]: Array<
    Option<K extends keyof TSelects ? TSelects[K] : never>
  >;
};

type SelectMode = "single" | "multi";

type SelectedOf<
  TModes extends Record<string, SelectMode>,
  TSelects extends Record<string, string | number>,
> = {
  [K in keyof TModes]: K extends keyof TSelects
    ? TModes[K] extends "multi"
      ? Array<TSelects[K]> | null
      : TSelects[K] | null
    : never;
};

/* ============== Unified resources ============== */
type ResourceApi<TParams, TData> = (params: TParams) => Promise<TData>;

type ResourceConfigs<TParams, TResources extends Record<string, unknown>> = {
  [K in keyof TResources]: {
    api: ResourceApi<TParams, TResources[K]>;
    shouldAutoRun?: (next: TParams, prev: TParams | null) => boolean;
    showLoadingOnRefetch?: boolean;
  };
};

/* ============== UI state & actions ============== */
type ReportState<
  TModes extends Record<string, SelectMode>,
  TSelects extends Record<keyof TModes, string | number>,
  TView extends Record<string, unknown> = Record<string, never>,
> = {
  searchQuery: string;
  dateRange: DateRange;
  selected: SelectedOf<TModes, TSelects>;
  page: number;
  pageSize: number;
  sortBy: string | null;
  sortOrder: SortOrder | null;
  /**
   * Page-defined, client-only view state (column visibility, modal-open
   * flags, multi-column table sort, …). Never sent to the backend and not
   * part of `deriveParams`. Defaults to an empty object for pages that don't
   * need it.
   */
  view: TView;
};

type ReportActions<
  TModes extends Record<string, SelectMode>,
  TSelects extends Record<keyof TModes, string | number>,
  TView extends Record<string, unknown> = Record<string, never>,
> = {
  setSearchQuery: (q: string) => void;
  setDateRange: (range: DateRange) => void;
  clearDateRange: () => void;
  setSelected: <K extends keyof TModes>(
    key: K,
    value: SelectedOf<TModes, TSelects>[K],
  ) => void;
  clearSelected: <K extends keyof TModes>(key: K) => void;
  clearAllSelected: () => void;
  setPage: (p: number) => void;
  setPageSize: (n: number) => void;
  setSort: (by: string | null, order: SortOrder | null) => void;
  /** Merge-patch the page-defined view state (see `ReportState.view`). */
  setView: (patch: Partial<TView> | ((prev: TView) => Partial<TView>)) => void;
};

/* ============== Provider props & context ============== */
/**
 * Props for the Report Provider.
 */
type ReportProviderProps<
  TModes extends Record<string, SelectMode>,
  TSelects extends Record<keyof TModes, string | number>,
  TResources extends Record<string, unknown>,
  TParams extends { selected: SelectedOf<TModes, TSelects> },
  TView extends Record<string, unknown> = Record<string, never>,
> = PropsWithChildren<{
  /**
   * Convert current UI state into backend request params.
   * Runs on every relevant state change; NOT exposed to consumers.
   * Keep it pure & stable (wrap in useCallback if capturing outer vars).
   */
  deriveParams: (state: ReportState<TModes, TSelects, TView>) => TParams;

  /**
   * Unified resource config map (table, charts, export…).
   * Each entry provides:
   *   - `api(params, signal)`
   *   - optional `shouldAutoRun(nextParams, prevParams)`:
   *       * provided  → resource is AUTO and runs when this returns true
   *       * undefined → resource is MANUAL and only runs via `runResource(key)`
   */
  resources: ResourceConfigs<TParams, TResources>;

  /**
   * Prefetched dropdown options keyed by the modes keys (canonical).
   * Pass what you loaded outside the provider (SSR/CSR). Read-only in context.
   */
  selectOptions: OptionsValues<TModes, TSelects>;

  /**
   * Initial UI seeds (used once on first mount).
   * Safe place to prefill dateRange, page, preselected dropdown values, and
   * the initial `view` state.
   */
  initialState?: Partial<
    Omit<ReportState<TModes, TSelects, TView>, "selected">
  > & {
    selected?: Partial<SelectedOf<TModes, TSelects>>;
  };

  /**
   * Fire when the "params" of state changes (after mount):
   *   { page, pageSize, dateRange }
   * Use for analytics/logging/URL sync. Not called on initial mount.
   */
  onParamsChange?: (next: TParams) => void;
}>;

type ReportContextDef<
  TModes extends Record<string, SelectMode>,
  TSelects extends Record<keyof TModes, string | number>,
  TResources extends Record<string, unknown>,
  TParams extends { selected: SelectedOf<TModes, TSelects> },
  TView extends Record<string, unknown> = Record<string, never>,
> = {
  Provider: React.FC<
    ReportProviderProps<TModes, TSelects, TResources, TParams, TView>
  >;
  useReportContext: () => ReportContextValue<
    TModes,
    TSelects,
    TResources,
    TView
  >;
};

export type InferReport<
  T,
  K extends "modes" | "selects" | "resources" | "params" | "states" | "view",
> =
  T extends ReportContextDef<infer M, infer S, infer R, infer P, infer V>
    ? {
        modes: M;
        selects: S;
        resources: R;
        params: P;
        states: ReportState<M, S, V>;
        view: V;
      }[K]
    : never;

type ReportContextValue<
  TModes extends Record<string, SelectMode>,
  TSelects extends Record<keyof TModes, string | number>,
  TResources extends Record<string, unknown>,
  TView extends Record<string, unknown> = Record<string, never>,
> = {
  state: ReportState<TModes, TSelects, TView>;
  actions: ReportActions<TModes, TSelects, TView>;

  /** Async states for all resources (table, charts, export, etc.) */
  resources: { [K in keyof TResources]: AsyncData<TResources[K]> };

  /** Triggers the resource's API and updates its state */
  runResource: <K extends keyof TResources>(key: K) => Promise<void>;

  /** Read-only dropdown options provided by props */
  options: OptionsValues<TModes, TSelects>;
};

/* ============== Factory ============== */

/**
 * Module-level registry so that HMR re-evaluations of consumer modules reuse
 * the same React context object rather than creating a new one (which would
 * break any Provider/consumer pair still mounted in the tree).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const _ctxRegistry = new Map<string, React.Context<any>>();

export function createReportContext<
  const TModes extends Record<string, SelectMode>,
>(selectModes: TModes, contextKey?: string) {
  return <
    TSelects extends Record<keyof TModes, string | number>,
    TResources extends Record<string, unknown>,
    TParams extends { selected: SelectedOf<TModes, TSelects> },
    TView extends Record<string, unknown> = Record<string, never>,
  >(): ReportContextDef<TModes, TSelects, TResources, TParams, TView> => {
    const Ctx: React.Context<ReportContextValue<
      TModes,
      TSelects,
      TResources,
      TView
    > | null> =
      contextKey && _ctxRegistry.has(contextKey)
        ? _ctxRegistry.get(contextKey)!
        : (() => {
            const ctx = createContext<ReportContextValue<
              TModes,
              TSelects,
              TResources,
              TView
            > | null>(null);
            if (contextKey) _ctxRegistry.set(contextKey, ctx);
            return ctx;
          })();

    function Provider({
      deriveParams,
      resources,
      selectOptions,
      initialState,
      onParamsChange,
      children,
    }: ReportProviderProps<TModes, TSelects, TResources, TParams, TView>) {
      /* ---- defaults for dropdown selected ---- */
      const defaultSelected = useMemo(
        () =>
          Object.keys(selectModes).reduce(
            (aggr, key) => ({ ...aggr, [key]: null }),
            {} as SelectedOf<TModes, TSelects>,
          ),
        [],
      );

      const { selected: initSelected, ...restInit } = initialState ?? {};

      /* ---- UI state ---- */
      const [state, setState] = useState<ReportState<TModes, TSelects, TView>>(
        () => ({
          searchQuery: "",
          dateRange: { from: null, to: null },
          page: 0,
          pageSize: 10,
          sortBy: null,
          sortOrder: null,
          view: {} as TView,
          ...restInit,
          selected: {
            ...defaultSelected,
            ...(initSelected as Partial<SelectedOf<TModes, TSelects>>),
          },
        }),
      );

      const actions: ReportActions<TModes, TSelects, TView> = useMemo(
        () => ({
          setSearchQuery: (q) =>
            setState((s) => ({ ...s, searchQuery: q, page: 0 })),
          setDateRange: (range) =>
            setState((s) => ({ ...s, dateRange: range, page: 0 })),
          clearDateRange: () =>
            setState((s) => ({
              ...s,
              dateRange: { from: null, to: null },
              page: 0,
            })),
          setSelected: (key, value) =>
            setState((s) => ({
              ...s,
              selected: { ...s.selected, [key]: value },
              page: 0,
            })),
          clearSelected: (key) =>
            setState((s) => ({
              ...s,
              selected: { ...s.selected, [key]: null },
              page: 0,
            })),
          clearAllSelected: () =>
            setState((s) => ({
              ...s,
              selected: { ...defaultSelected },
              page: 0,
            })),
          setPage: (p) => setState((s) => ({ ...s, page: p })),
          setPageSize: (n) => setState((s) => ({ ...s, pageSize: n, page: 0 })),
          setSort: (by, order) =>
            setState((s) => ({
              ...s,
              sortBy: by,
              sortOrder: order,
              page: 0,
            })),
          setView: (patch) =>
            setState((s) => ({
              ...s,
              view: {
                ...s.view,
                ...(typeof patch === "function" ? patch(s.view) : patch),
              },
            })),
        }),
        [defaultSelected],
      );

      /* ---- params ---- */
      const params = useMemo(() => deriveParams(state), [deriveParams, state]);

      /* -- call onQueryChange when page/pageSize/dateRange change -- */
      useEffect(() => {
        onParamsChange && onParamsChange(params);
      }, [onParamsChange, params]);

      /* ---- resources state ---- */
      const [resourcesState, setResourcesState] = useState(() =>
        Object.keys(resources).reduce(
          (aggr, key) => ({
            ...aggr,
            [key]: { status: "idle" },
          }),
          {} as { [K in keyof TResources]: AsyncData<TResources[K]> },
        ),
      );

      // per-resource last-run params (to pass to shouldAutoRun)
      const lastParamsPerResourceRef = useRef<
        Partial<Record<keyof TResources, TParams>>
      >({});

      const latestRequestPerResourceRef = useRef<
        Partial<Record<keyof TResources, number>>
      >({});

      const runResourceCore = useCallback(
        async <K extends keyof TResources>(key: K, resourceParams: TParams) => {
          const cfg = resources[key];
          if (!cfg) return;

          const requestId = (latestRequestPerResourceRef.current[key] ?? 0) + 1;
          latestRequestPerResourceRef.current[key] = requestId;
          const isSuperseded = () =>
            latestRequestPerResourceRef.current[key] !== requestId;

          setResourcesState((prev) => {
            const hasPreviousData =
              (prev[key]?.status === "success" ||
                prev[key]?.status === "error") &&
              prev[key].data !== undefined;

            if (hasPreviousData && !cfg.showLoadingOnRefetch) {
              return prev;
            }

            return {
              ...prev,
              [key]: { status: "loading", data: prev[key]?.data },
            };
          });
          try {
            const data = await cfg.api(resourceParams);
            if (isSuperseded()) return;
            setResourcesState((prev) => {
              return {
                ...prev,
                [key]: { status: "success", data, updatedAt: Date.now() },
              };
            });
          } catch (error) {
            if (isSuperseded()) return;
            setResourcesState((prev) => {
              return {
                ...prev,
                [key]: { status: "error", error, data: prev[key]?.data },
              };
            });
          }
        },
        [resources],
      );

      const runResource = useCallback(
        async <K extends keyof TResources>(key: K) => {
          lastParamsPerResourceRef.current[key] = params; // memorize params for MANUAL
          await runResourceCore(key, params);
        },
        [params, runResourceCore],
      );

      // auto-run resources whose shouldAutoRun returns true
      useEffect(() => {
        for (const key of Object.keys(resources) as Array<keyof TResources>) {
          const def = resources[key];
          if (!def?.shouldAutoRun) continue; // skip MANUAL resources

          const prevParams = lastParamsPerResourceRef.current[key] ?? null;
          if (def.shouldAutoRun(params, prevParams)) {
            lastParamsPerResourceRef.current[key] = params; // memorize params for AUTO
            void runResourceCore(key, params);
          }
        }
      }, [params, resources, runResourceCore]);

      return (
        <Ctx.Provider
          value={{
            state,
            actions,
            resources: resourcesState,
            runResource,
            options: selectOptions,
          }}
        >
          {children}
        </Ctx.Provider>
      );
    }

    const useReportContext = () => {
      const ctx = useContext(Ctx);
      if (!ctx)
        throw new Error("useReportContext must be used within its Provider");
      return ctx;
    };

    return { Provider, useReportContext };
  };
}
